import { getErrorType } from '@src/utils/api-helpers';
import { emitReturnsBusinessError } from '@src/utils/returns-instrumentation-context';
import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import {
  setSelectedItems,
  setIsMultiAddressReturn,
  setReturnShipments,
  setPolicyValidatorResponse,
  useReturnStore,
  setFraudDecisions,
  IReturnShipments,
} from '@src/stores/returnStore';
import {
  generatePolicyValidatorRequest,
  generatePolicyValidatorVerifyObject,
  generateShippingRequestObject,
  PolicyValidatorErrors,
  generateInStoreShipment,
  checkForIneligibleItems,
} from '@src/utils';
import {
  IPolicyValidatorRequest,
  IReturnEligibilityResponse,
} from '@src/models/api';
import { PolicyValidatorAPI } from '@src/api/returns';

import {
  processPolicyValidatorShipments,
  processAprissVerifyDecision,
  composeReturnShipments,
  collectLineIdentifiers,
  collectLineAddresses,
} from '@src/utils/itemsToReturnUtils';
import { adaptReturnMethodsResponse } from '@src/utils/eligibility-utils';
import {
  TagPolicyValidatorApiError,
  TagPolicyValidatorMultiAddress,
} from '@src/utils/analytics-tagging';
import {
  ItemToReturn,
  ReturnsFraudPrompt,
  OrderDetailsSessionDataV2,
} from '@src/models/returns';
import { ReturnFraudType } from '@src/constants/analytics';
import { IGenericError } from '@src/models/errors';
import { RETURN_METHOD } from '@src/constants/general';
import { getUniqueMethods } from '@src/utils/general';
import { setChosenReturnMethods } from '@src/stores/newFlowStore';
import { getUserId, getSvocId } from '@src/utils/user';
import { isROSEnabled } from '@src/constants/fs-values';

/**
 * Handed to the validator's shipment builder when the composer is going to
 * raise the multi-address tag itself. The builder can only see its own share
 * of the legs, so letting it raise the tag would under-report.
 */
const skipMultiAddressTag = () => undefined;

interface UseProcessValidatorProps {
  orderDetailsV2: OrderDetailsSessionDataV2 | undefined;
  returnEligibilityDetails: IReturnEligibilityResponse | undefined;
  isMilitaryExchangeGuest: boolean;
  proceedToNextStep: () => void;
}

export function useProcessValidator({
  orderDetailsV2,
  returnEligibilityDetails,
  isMilitaryExchangeGuest,
  proceedToNextStep,
}: UseProcessValidatorProps) {
  const [isPolicyValidatorLoading, setIsPolicyValidatorLoading] =
    useState(false);
  const [policyValidatorAlert, setPolicyValidatorAlert] =
    useState<PolicyValidatorErrors>(PolicyValidatorErrors.NONE);
  const currentShipments = useReturnStore((state) => state.returnShipments);

  // APRISS Fraud Modals
  const [renderModal, setRenderModal] = useState<ReturnsFraudPrompt>();
  const [returnFraudType, setReturnFraudType] = useState<
    ReturnFraudType | undefined
  >();

  const closeModal = () => {
    setRenderModal(undefined);
  };
  const processPolicyValidator = useCallback(
    async (selectedItems: ItemToReturn[]) => {
      try {
        if (!orderDetailsV2) throw new Error('No Order Details');
        if (!returnEligibilityDetails)
          throw new Error('No Return Eligibility Response');

        const orderId = orderDetailsV2.orderNumber;
        const { et } = returnEligibilityDetails;
        // Filter out in-store items for policy validator call, throw error if no return method
        const modifiedSelectedItems = selectedItems
          .filter((item) => item.returnMethod !== RETURN_METHOD.IN_STORE)
          .map((item: ItemToReturn) => {
            if (!item.returnMethod)
              throw new Error('Return Method is missing for one or more items');
            return {
              ...item,
              returnMethod: item.returnMethod,
            };
          });

        const ssrHrtvReqLineItem = generatePolicyValidatorRequest(
          modifiedSelectedItems,
          orderDetailsV2.deliveryDictionary,
          orderDetailsV2.fulfillmentGroups,
        );
        const shippingRequest = generateShippingRequestObject(
          orderDetailsV2,
          modifiedSelectedItems,
        );
        const verifyObject = generatePolicyValidatorVerifyObject(
          orderDetailsV2,
          returnEligibilityDetails,
          modifiedSelectedItems,
          shippingRequest,
        );
        const request: IPolicyValidatorRequest = {
          verify: verifyObject,
          ssrHrtvRequest: {
            et,
            ssrHrtvReqLineItem,
            orderId,
            emailId: orderDetailsV2.emailId,
            orderDate: orderDetailsV2.createDateTime,
            userId: getUserId() || '',
            svocId: getSvocId() || '',
          },
          isGuest: isMilitaryExchangeGuest,
          net: orderDetailsV2.net || '',
        };
        setIsPolicyValidatorLoading(true);

        const response = await PolicyValidatorAPI(request);
        const policyValidatorResponse = response.data;
        const { status, ssrHRTVResponse, multiVerifyDecision } =
          policyValidatorResponse?.returnService || {};
        if (!policyValidatorResponse) {
          throw new Error('PolicyValidatorAPI: Empty 2xx Response');
        }

        const isValidNetworkResponse = status === 'success' && ssrHRTVResponse;
        if (!isValidNetworkResponse) {
          throw new Error(
            'PolicyValidatorAPI: Invalid PolicyValidator Response',
          );
        }

        const hasIneligibleItem = checkForIneligibleItems(
          ssrHRTVResponse.shipments,
        );

        // Under ROS the fraud check has already answered, on the return methods
        // response back at the quantity step, and the customer has already been
        // shown whatever it said. The validator answers a second time here, so
        // reading it again would prompt twice for one decision and report the
        // same return to the fraud vendor twice. The validator itself keeps
        // running: it still supplies the shipments, the token and the Keep It
        // flag, none of which the methods response carries.
        //
        // Not obvious from here: skipping this also skips the write of the
        // per-leg fraud decisions, because that write lives inside it. The one
        // thing still reading them is the Keep It report, which stays on the
        // legacy shape in both switch states — so under ROS it selects from an
        // empty list and takes its default. Same value either way, since a Keep
        // It return is a single leg and the single-leg case yields that default
        // anyway; worth knowing before assuming the selection still derives
        // something.
        const fraudType = isROSEnabled()
          ? undefined
          : processAprissVerifyDecision(
              hasIneligibleItem,
              setReturnFraudType,
              setRenderModal,
              closeModal,
              proceedToNextStep,
              setFraudDecisions,
              multiVerifyDecision,
            );

        // New Relic: fraud/HRTV decision (DENIED / WARNED) — a hard user block.
        if (fraudType) {
          emitReturnsBusinessError(String(fraudType), {
            source: 'fraud_decision',
          });
        }

        if (hasIneligibleItem && !fraudType) {
          // throw error, unless fraud modal is being shown
          //
          // Under ROS there is never a modal here to suppress it, so ineligible
          // items always surface. That is the intended reading rather than a
          // side effect: a customer who accepted a warning earlier and then
          // turns out to have items the validator will not take should be told
          // so, where previously the second warning masked it and waved them
          // through.
          throw new Error('HAS_INELIGIBLE_ITEMS');
        }

        setIsPolicyValidatorLoading(false);
        setPolicyValidatorAlert(PolicyValidatorErrors.NONE);
        // Read at call time rather than from a render closure: this runs in
        // the same tick as the method step's Continue, and the answer was
        // written a step earlier.
        const { returnMethodsResponse } = useReturnStore.getState();
        // Present exactly when the legs are to be composed from the service's
        // answer rather than read off the validator's grouping.
        //
        // Testing the answer as well as the switch is the type-level response
        // to a state the quantity step already prevents — under ROS it will
        // not let the customer reach this step until the answer has landed.
        // Falling back to the validator's grouping is the safe reading of an
        // answer that somehow is not there: composing without one would mark
        // every item ineligible.
        const adaptedMethods =
          isROSEnabled() && returnMethodsResponse
            ? adaptReturnMethodsResponse(returnMethodsResponse)
            : undefined;

        // Rewrite this to include all types of returns, not just multi-address, start by removing uses and then the isMultiAddress flag
        // Use current shipments from store to preserve user-entered states
        //
        // When composing, the composer raises the multi-address tag instead,
        // counted across both sources of legs. This call is asked not to raise
        // it because it can only see the validator's own share of them.
        const validatorShipments = processPolicyValidatorShipments(
          policyValidatorResponse,
          adaptedMethods ? skipMultiAddressTag : TagPolicyValidatorMultiAddress,
          currentShipments,
        );

        let modifiedReturnShipments: IReturnShipments[];
        if (adaptedMethods) {
          modifiedReturnShipments = composeReturnShipments(
            selectedItems,
            adaptedMethods,
            TagPolicyValidatorMultiAddress,
            currentShipments,
            {
              // Pickup only. Drop-off grouping is moving to the shipping
              // service's answer rather than the composer's address key, so
              // its legs are carried over from the validator until then.
              methods: [RETURN_METHOD.LORP],
              carriedOverLegs: validatorShipments.filter(
                (shipment) => shipment.shipmentType === 'DROPOFF',
              ),
              // The create call still reads the RTV token and its companions
              // off the leg, and the methods answer has no home for them yet.
              // Taking them from the validator keeps the create payload
              // unchanged, so only the grouping moves.
              lineIdentifiers: collectLineIdentifiers(validatorShipments),
              // The methods contract marks the per-method address optional and
              // its endpoint is not built, so a composed leg can arrive with
              // no destination — which the create call would then submit. The
              // validator's answer for the same line stands in.
              fallbackAddresses: collectLineAddresses(validatorShipments),
            },
          );
        } else {
          modifiedReturnShipments = validatorShipments;
          // Build and append a new shipments object for in-store returns if necessary
          const hasInStoreReturn = selectedItems.some(
            (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
          );

          if (hasInStoreReturn) {
            const inStoreShipment = generateInStoreShipment(
              selectedItems,
              modifiedReturnShipments,
            );
            modifiedReturnShipments.push(inStoreShipment);
          }
        }

        // Will probably need to set returnShipments here to prevent race condition with processPolicyValidatorShipments
        setReturnShipments(modifiedReturnShipments);
        // updates return methods to the shipments
        setChosenReturnMethods(getUniqueMethods(selectedItems));

        setPolicyValidatorResponse(ssrHRTVResponse);
        return policyValidatorResponse;
      } catch (error: unknown) {
        const axiosError = error as AxiosError<IGenericError>;
        // We don't want an additional tag for partial item eligibility errors here, as they were already tagged in the policyValidator call
        if (axiosError?.message !== 'PARTIAL_INELIGIBLE_ITEMS') {
          // Tagging Analytics when an error is thrown
          TagPolicyValidatorApiError(axiosError);
        }
        setIsPolicyValidatorLoading(false);
        const errorType = getErrorType(axiosError);
        // New Relic: policy-validator business rejection that dead-ends the user.
        // Emit ONLY coded rejections — NETWORK/GENERIC are real 5xx/network/timeout
        // (or uncoded throws) that AjaxRequest already captures, so emitting them here
        // would pollute the coded-rejection dataset and double-count (see PR contract).
        const isCodedRejection =
          errorType !== PolicyValidatorErrors.NETWORK &&
          errorType !== PolicyValidatorErrors.GENERIC;
        if (
          axiosError?.message !== 'PARTIAL_INELIGIBLE_ITEMS' &&
          isCodedRejection
        ) {
          emitReturnsBusinessError(errorType, {
            source: 'policy_validator',
          });
        }
        setPolicyValidatorAlert(errorType);
        return undefined;
      }
    },
    [
      orderDetailsV2,
      returnEligibilityDetails,
      isMilitaryExchangeGuest,
      setSelectedItems,
      setReturnFraudType,
      setRenderModal,
      closeModal,
      proceedToNextStep,
      setIsPolicyValidatorLoading,
      setPolicyValidatorAlert,
      setIsMultiAddressReturn,
      setReturnShipments,
      setPolicyValidatorResponse,
    ],
  );

  return {
    processPolicyValidator,
    policyValidatorAlert,
    renderModal,
    returnFraudType,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  };
}
